"use client";

import Image from "next/image";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { APP_VERSION_SEMVER } from "@/lib/constants/version";
import { PublicLink as Link } from "@/components/public/PublicLink";

export function Footer() {
  const { t } = useJsonTranslation("Footer");
  // Pin to the deployed-build semver so every page shows the same number.
  // The `useDesktopDisplayVersion` hook fetched the latest changelog entry,
  // which raced across pages and produced visibly different version
  // numbers in the footer between tabs / navigations.
  const displayVersion = APP_VERSION_SEMVER;

  const footerLinks = {
    product: [
      { labelKey: "home.footer.product.features", href: "/features" },
      { labelKey: "home.footer.product.pricing", href: "/seeds" },
      { labelKey: "home.footer.product.howItWorks", href: "/how-to" },
      {
        labelKey: "home.footer.product.gettingStarted",
        href: "/getting-started",
      },
      { labelKey: "home.footer.product.download", href: "/download" },
      { labelKey: "home.footer.product.support", href: "/contact" },
    ],
    company: [
      { labelKey: "home.footer.company.about", href: "/about" },
      { labelKey: "home.footer.company.blog", href: "/blog" },
      { labelKey: "home.footer.company.changelog", href: "/changelog" },
      { labelKey: "home.footer.company.contact", href: "/contact" },
    ],
    legal: [
      { labelKey: "home.footer.legal.privacyPolicy", href: "/privacy-policy" },
      {
        labelKey: "home.footer.legal.termsOfService",
        href: "/terms-of-service",
      },
      { labelKey: "home.footer.legal.refundPolicy", href: "/refund-policy" },
      {
        labelKey: "home.footer.legal.intellectualProperty",
        href: "/intellectual-property",
      },
      {
        labelKey: "home.footer.legal.doNotSell",
        href: "/privacy-policy#do-not-sell",
      },
    ],
  };

  return (
    <footer className="border-border/30 border-t px-4 py-20 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-12 md:grid-cols-4">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link href="/" className="flex items-center gap-2">
              <Image
                src="/logo-icon.png"
                alt="VidSeeds.ai"
                width={32}
                height={32}
                className="h-8 w-auto"
                priority
              />
              <span className="font-display text-foreground text-2xl font-bold tracking-tight">
                VidSeeds
                <span className="text-primary">.ai</span>
              </span>
            </Link>
            <p className="text-muted-foreground/60 mt-4 text-sm leading-relaxed">
              {t("home.footer.description")}
            </p>
            <p className="text-muted-foreground/50 mt-4 text-xs">
              {t("home.footer.patentPending")}
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-foreground mb-4 font-semibold">
              {t("home.footer.productTitle")}
            </h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-foreground mb-4 font-semibold">
              {t("home.footer.companyTitle")}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-foreground mb-4 font-semibold">
              {t("home.footer.legalTitle")}
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-border/20 mt-14 flex flex-col items-center justify-between gap-4 border-t pt-8 sm:flex-row">
          <p className="text-muted-foreground/50 text-sm">
            {t("home.footer.copyright", { year: new Date().getFullYear() })}{" "}
            <span className="text-muted-foreground/30 ml-2">
              v{displayVersion}
            </span>
          </p>
          <div className="text-muted-foreground/50 flex items-center gap-6 text-sm">
            <span>{t("home.footer.languages")}</span>
            <span>•</span>
            <span>{t("home.footer.madeWithAI")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
